/**
 * The Primary Visit — one consultation kind that replaces picking a template.
 *
 * The doctor records the consultation and gets a protocol back. Which sections
 * that protocol has, which answers fill them, and what a doctor is required to
 * measure are fixed here rather than per template, because a primary visit is
 * the same document in every speciality.
 *
 * Three things reach the report without the doctor saying them out loud:
 * the standard objective status, the examination plan and the treatment plan.
 * None of them are the model's opinion — the status is a printed norm, the two
 * plans come from the clinical protocol of the confirmed diagnosis — and none
 * of them are the doctor's words either. So every value carries where it came
 * from (`FieldSource`) and the ones that are not the doctor's own must be
 * confirmed before the report can be submitted.
 */
import type { LocalizedText } from "./types";

/** Section keys of the primary-visit document, in the order they are written. */
export const PRIMARY_VISIT_SECTIONS = [
  "complaints",
  "anamnesisDisease",
  "anamnesisLife",
  "objectiveStatus",
  "diagnosis",
  "plan",
] as const;

export type PrimaryVisitSectionKey = (typeof PRIMARY_VISIT_SECTIONS)[number];

/**
 * Where a value in the report came from.
 *
 * - `transcript` — the doctor said it. Needs no confirmation.
 * - `default`    — the printed norm, filled in because nothing was said.
 * - `protocol`   — taken from the clinical protocol of the diagnosis.
 * - `doctor`     — the doctor typed or corrected it by hand.
 */
export const FIELD_SOURCES = ["transcript", "default", "protocol", "doctor"] as const;
export type FieldSource = (typeof FIELD_SOURCES)[number];

/** A value the doctor has to accept before the report leaves their hands. */
export function requiresConfirmation(source: FieldSource): boolean {
  return source === "default" || source === "protocol";
}

/**
 * Physical measurements. Mandatory on every primary visit regardless of what
 * the consultation was about — a protocol without vitals is not a protocol.
 * `bmi` is derived, so it is not asked for separately.
 */
export const VITAL_FIELD_KEYS = [
  "temperature",
  "bloodPressure",
  "heartRate",
  "respiratoryRate",
  "spo2",
  "height",
  "weight",
] as const;

export type VitalFieldKey = (typeof VITAL_FIELD_KEYS)[number];

/** Answer keys that make up the diagnosis section. */
export const DIAGNOSIS_FIELD_KEYS = [
  "diagnosisMain",
  "diagnosisIcd10",
  "diagnosisConcomitant",
  "diagnosisComplications",
] as const;

/** Answer keys that make up the plan section. */
export const PLAN_FIELD_KEYS = ["examinationPlan", "treatmentPlan"] as const;

/**
 * Printed when a section of the diagnosis has nothing in it. The protocol form
 * carries all three lines whether or not they are filled, so an empty one is a
 * dash, never a missing line.
 */
export const DIAGNOSIS_PLACEHOLDER = "—";

/**
 * The standard objective status — what a doctor examines and does not dictate
 * because it was normal. Offered as a whole; the doctor accepts or edits it.
 *
 * Kept as data rather than asked from the model on purpose: this is a printed
 * norm, so it must read identically on every report that uses it.
 */
export const DEFAULT_OBJECTIVE_STATUS: LocalizedText = {
  ru:
    "Общее состояние удовлетворительное. Сознание ясное, положение активное. " +
    "Кожные покровы и видимые слизистые обычной окраски, чистые, умеренно влажные. " +
    "Периферические лимфатические узлы не увеличены. Отёков нет. " +
    "Дыхание везикулярное, хрипов нет. Тоны сердца ясные, ритм правильный. " +
    "Язык влажный, чистый. Живот мягкий, безболезненный при пальпации. " +
    "Печень и селезёнка не увеличены. Симптом поколачивания отрицательный с обеих сторон. " +
    "Физиологические отправления в норме.",
  kz:
    "Жалпы жағдайы қанағаттанарлық. Сана айқын, қалпы белсенді. " +
    "Тері жамылғысы мен көзге көрінетін шырышты қабаттар қалыпты түсті, таза, орташа ылғалды. " +
    "Шеткі лимфа түйіндері ұлғаймаған. Ісіну жоқ. " +
    "Тыныс алуы везикулалық, сырыл жоқ. Жүрек тондары айқын, ырғағы дұрыс. " +
    "Тілі ылғалды, таза. Іші жұмсақ, пальпацияда ауырсынусыз. " +
    "Бауыры мен көкбауыры ұлғаймаған. Бүйрек тұсын қағу белгісі екі жағынан теріс. " +
    "Физиологиялық қызметтері қалыпты.",
  en:
    "General condition satisfactory. Consciousness clear, position active. " +
    "Skin and visible mucous membranes of normal colour, clean, moderately moist. " +
    "Peripheral lymph nodes not enlarged. No oedema. " +
    "Vesicular breathing, no rales. Heart sounds clear, rhythm regular. " +
    "Tongue moist and clean. Abdomen soft, non-tender on palpation. " +
    "Liver and spleen not enlarged. Costovertebral angle tenderness negative bilaterally. " +
    "Physiological functions normal.",
};

/** Sections of the primary-visit document, in document order. */
export const PRIMARY_VISIT_SECTION_DEFS: {
  key: PrimaryVisitSectionKey;
  title: LocalizedText;
  order: number;
  render: "narrative" | "list";
  hint: LocalizedText;
}[] = [
  {
    key: "complaints",
    order: 1,
    render: "narrative",
    title: { ru: "Жалобы", kz: "Шағымдар", en: "Complaints" },
    hint: {
      ru: "Связный абзац от третьего лица: «Пациент предъявляет жалобы на…». Не перечисление через запятую.",
      kz: "Үшінші жақтан жазылған тұтас абзац: «Науқас … шағымданады». Үтірмен тізім емес.",
      en: "A coherent third-person paragraph, not a comma-separated list.",
    },
  },
  {
    key: "anamnesisDisease",
    order: 2,
    render: "narrative",
    title: { ru: "Анамнез заболевания", kz: "Аурудың анамнезі", en: "History of present illness" },
    hint: {
      ru: "Начало, развитие, чем лечился, с чем связывает, почему обратился сейчас.",
      kz: "Басталуы, дамуы, қандай ем қабылдағаны, немен байланыстыратыны, қазір неге келгені.",
      en: "Onset, progression, prior treatment, reason for the current visit.",
    },
  },
  {
    key: "anamnesisLife",
    order: 3,
    render: "narrative",
    title: { ru: "Анамнез жизни", kz: "Өмір анамнезі", en: "Past medical history" },
    hint: {
      ru: "Хронические заболевания, перенесённые операции, аллергоанамнез, вредные привычки, наследственность.",
      kz: "Созылмалы аурулар, бастан өткен операциялар, аллергоанамнез, зиянды әдеттер, тұқымқуалаушылық.",
      en: "Chronic conditions, past surgery, allergy history, habits, family history.",
    },
  },
  {
    key: "objectiveStatus",
    order: 4,
    render: "narrative",
    title: { ru: "Объективный статус", kz: "Объективті статус", en: "Objective status" },
    hint: {
      ru: "Физикальные показатели, затем осмотр по системам. Показатели пишутся в виде «название — значение».",
      kz: "Физикалық көрсеткіштер, содан кейін жүйелер бойынша қарау. Көрсеткіштер «атауы — мәні» түрінде жазылады.",
      en: "Vital signs first, then the examination by system. Vitals stay in name — value form.",
    },
  },
  {
    key: "diagnosis",
    order: 5,
    render: "list",
    title: { ru: "Предварительный диагноз", kz: "Алдын ала диагноз", en: "Preliminary diagnosis" },
    hint: {
      ru: "Три строки всегда: основной, сопутствующий, осложнения. Пустая строка печатается прочерком.",
      kz: "Әрқашан үш жол: негізгі, қосалқы, асқынулар. Бос жол сызықшамен басылады.",
      en: "Always three lines: main, concomitant, complications. An empty one prints as a dash.",
    },
  },
  {
    key: "plan",
    order: 6,
    render: "list",
    title: {
      ru: "План обследования и лечения",
      kz: "Тексеру және емдеу жоспары",
      en: "Examination and treatment plan",
    },
    hint: {
      ru: "Сначала обследования, затем лечение. Каждый пункт с новой строки, без маркеров.",
      kz: "Алдымен тексерулер, содан кейін емдеу. Әр тармақ жаңа жолдан, маркерсіз.",
      en: "Investigations first, then treatment. One item per line, no bullet characters.",
    },
  },
];

/**
 * The questionnaire of a primary visit: what is extracted from the recording.
 *
 * `importance: "blocking"` is what the clinic means by "обязательные" — the
 * report cannot go to audit while one is empty. The vitals are all blocking;
 * the anamnesis is not, because a patient with nothing to report is normal.
 */
export const PRIMARY_VISIT_FIELDS: {
  key: string;
  label: LocalizedText;
  bilateral: false;
  importance: "blocking" | "normal" | "extra";
  type: "text";
  sectionKey: PrimaryVisitSectionKey;
}[] = [
  {
    key: "complaints",
    sectionKey: "complaints",
    importance: "blocking",
    label: { ru: "Жалобы", kz: "Шағымдар", en: "Complaints" },
    bilateral: false,
    type: "text",
  },
  {
    key: "anamnesisDisease",
    sectionKey: "anamnesisDisease",
    importance: "normal",
    label: { ru: "Анамнез заболевания", kz: "Аурудың анамнезі", en: "History of present illness" },
    bilateral: false,
    type: "text",
  },
  {
    key: "anamnesisLife",
    sectionKey: "anamnesisLife",
    importance: "normal",
    label: { ru: "Анамнез жизни", kz: "Өмір анамнезі", en: "Past medical history" },
    bilateral: false,
    type: "text",
  },
  {
    key: "allergyHistory",
    sectionKey: "anamnesisLife",
    importance: "normal",
    label: { ru: "Аллергологический анамнез", kz: "Аллергологиялық анамнез", en: "Allergy history" },
    bilateral: false,
    type: "text",
  },
  {
    key: "temperature",
    sectionKey: "objectiveStatus",
    importance: "blocking",
    label: { ru: "Температура, °C", kz: "Температура, °C", en: "Temperature, °C" },
    bilateral: false,
    type: "text",
  },
  {
    key: "bloodPressure",
    sectionKey: "objectiveStatus",
    importance: "blocking",
    label: { ru: "АД, мм рт. ст.", kz: "АҚ, сын. бағ. мм", en: "Blood pressure, mmHg" },
    bilateral: false,
    type: "text",
  },
  {
    key: "heartRate",
    sectionKey: "objectiveStatus",
    importance: "blocking",
    label: { ru: "ЧСС, уд/мин", kz: "ЖЖЖ, соғ/мин", en: "Heart rate, bpm" },
    bilateral: false,
    type: "text",
  },
  {
    key: "respiratoryRate",
    sectionKey: "objectiveStatus",
    importance: "blocking",
    label: { ru: "ЧДД, в мин", kz: "ТАЖ, мин", en: "Respiratory rate, /min" },
    bilateral: false,
    type: "text",
  },
  {
    key: "spo2",
    sectionKey: "objectiveStatus",
    importance: "blocking",
    label: { ru: "SpO₂, %", kz: "SpO₂, %", en: "SpO₂, %" },
    bilateral: false,
    type: "text",
  },
  {
    key: "height",
    sectionKey: "objectiveStatus",
    importance: "blocking",
    label: { ru: "Рост, см", kz: "Бойы, см", en: "Height, cm" },
    bilateral: false,
    type: "text",
  },
  {
    key: "weight",
    sectionKey: "objectiveStatus",
    importance: "blocking",
    label: { ru: "Вес, кг", kz: "Салмағы, кг", en: "Weight, kg" },
    bilateral: false,
    type: "text",
  },
  {
    key: "bmi",
    sectionKey: "objectiveStatus",
    importance: "normal",
    label: { ru: "ИМТ", kz: "ДСИ", en: "BMI" },
    bilateral: false,
    type: "text",
  },
  {
    key: "objectiveStatus",
    sectionKey: "objectiveStatus",
    importance: "blocking",
    label: { ru: "Осмотр по системам", kz: "Жүйелер бойынша қарау", en: "Examination by system" },
    bilateral: false,
    type: "text",
  },
  {
    key: "localStatus",
    sectionKey: "objectiveStatus",
    importance: "extra",
    label: { ru: "Локальный статус", kz: "Жергілікті статус", en: "Local status" },
    bilateral: false,
    type: "text",
  },
  {
    key: "diagnosisMain",
    sectionKey: "diagnosis",
    importance: "blocking",
    label: { ru: "Основной диагноз", kz: "Негізгі диагноз", en: "Main diagnosis" },
    bilateral: false,
    type: "text",
  },
  {
    key: "diagnosisIcd10",
    sectionKey: "diagnosis",
    importance: "normal",
    label: { ru: "Код по МКБ-10", kz: "АХЖ-10 коды", en: "ICD-10 code" },
    bilateral: false,
    type: "text",
  },
  {
    key: "diagnosisConcomitant",
    sectionKey: "diagnosis",
    importance: "normal",
    label: { ru: "Сопутствующий диагноз", kz: "Қосалқы диагноз", en: "Concomitant diagnosis" },
    bilateral: false,
    type: "text",
  },
  {
    key: "diagnosisComplications",
    sectionKey: "diagnosis",
    importance: "normal",
    label: { ru: "Осложнения", kz: "Асқынулар", en: "Complications" },
    bilateral: false,
    type: "text",
  },
  {
    key: "examinationPlan",
    sectionKey: "plan",
    importance: "blocking",
    label: { ru: "План обследования", kz: "Тексеру жоспары", en: "Examination plan" },
    bilateral: false,
    type: "text",
  },
  {
    key: "treatmentPlan",
    sectionKey: "plan",
    importance: "blocking",
    label: { ru: "План лечения", kz: "Емдеу жоспары", en: "Treatment plan" },
    bilateral: false,
    type: "text",
  },
];

/** Body-mass index from height in cm and weight in kg, rounded to one decimal. */
export function computeBmi(
  heightCm: string | number | null | undefined,
  weightKg: string | number | null | undefined,
): string | null {
  const h = typeof heightCm === "string" ? parseFloat(heightCm.replace(",", ".")) : heightCm;
  const w = typeof weightKg === "string" ? parseFloat(weightKg.replace(",", ".")) : weightKg;
  if (!h || !w || !isFinite(h) || !isFinite(w) || h <= 0 || w <= 0) return null;
  const metres = h > 3 ? h / 100 : h; // accepts 172 and 1.72
  const bmi = w / (metres * metres);
  if (!isFinite(bmi) || bmi <= 0 || bmi > 200) return null;
  return (Math.round(bmi * 10) / 10).toFixed(1);
}

/**
 * Sections still carrying system-filled text nobody has taken responsibility for.
 *
 * The backend blocks submission on this and the report screen shows it, so it
 * lives here: two copies of the rule would eventually disagree, and the one the
 * doctor sees would stop matching the one that decides.
 *
 * The provenance map is the only input. It is tempting to also treat "the
 * answer appears in editedData" as the doctor having written it, but the
 * protocol plan is stored there too — that is the layer the report reads
 * overrides from — and a plan the system pulled in would then look like a
 * plan the doctor typed. Editing an answer by hand is recorded by flipping its
 * source to `doctor`, which is what releases it from needing confirmation;
 * replacing the standard objective status with what was actually found is a
 * stronger statement than ticking a box, and the source records that.
 */
export function unconfirmedSections(report: {
  fieldSources?: Record<string, string> | null;
  confirmations?: Record<string, unknown> | null;
}): PrimaryVisitSectionKey[] {
  const sources = report.fieldSources ?? {};
  const confirmations = report.confirmations ?? {};

  const sectionOf = new Map<string, PrimaryVisitSectionKey>(
    PRIMARY_VISIT_FIELDS.map((f) => [f.key, f.sectionKey]),
  );

  const pending = new Set<PrimaryVisitSectionKey>();
  for (const [key, source] of Object.entries(sources)) {
    if (source !== "default" && source !== "protocol") continue;
    const section = sectionOf.get(key);
    if (section && !confirmations[section]) pending.add(section);
  }
  return [...pending];
}
